import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import type { TransportSpendPoint } from '../api/types';
import { count, shortDate } from '../utils/format';

import { ChartTooltip } from './ChartTooltip';
import { Legend } from './Legend';
import { CHROME, SERIES, STATUS } from './palette';

const SCANS = SERIES[0];
const ESCALATIONS = SERIES[1];

/**
 * Scans attempted against escalations to the paid transport, per day. Both are
 * counts of requests, so they share one axis; grouped rather than stacked
 * because the interesting reading is the ratio between them, not the total.
 */
export function CrawlSpendChart({ points }: { points: TransportSpendPoint[] }) {
  if (points.length === 0) {
    return <div className="state">No crawl activity logged in this window.</div>;
  }

  const rows = points.map((point) => ({ ...point, label: shortDate(point.day) }));

  return (
    <>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={rows} margin={{ top: 8, right: 12, bottom: 4, left: 0 }} barGap={2}>
          <CartesianGrid stroke={CHROME.grid} vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: CHROME.inkMuted, fontSize: 11 }}
            axisLine={{ stroke: CHROME.axis }}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: CHROME.inkMuted, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={40}
            allowDecimals={false}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) {
                return null;
              }

              const row = payload[0].payload as (typeof rows)[number];
              const ratio =
                row.scansStarted === 0 ? null : (row.escalations / row.scansStarted).toFixed(2);

              return (
                <ChartTooltip
                  title={row.label}
                  rows={[
                    { key: 's', label: 'Scans started', value: count(row.scansStarted), color: SCANS },
                    {
                      key: 'e',
                      label: 'Paid escalations',
                      value: count(row.escalations),
                      color: ESCALATIONS,
                    },
                    { key: 'r', label: 'Escalations per scan', value: ratio ?? '—' },
                    { key: 'f', label: 'Fetch failures', value: count(row.failures) },
                    { key: 'b', label: 'Blocked scans', value: count(row.blocks) },
                  ]}
                />
              );
            }}
          />
          <Bar isAnimationActive={false} dataKey="scansStarted" fill={SCANS} radius={[4, 4, 0, 0]} maxBarSize={26} />
          <Bar
            isAnimationActive={false}
            dataKey="escalations"
            fill={ESCALATIONS}
            radius={[4, 4, 0, 0]}
            maxBarSize={26}
          />
        </BarChart>
      </ResponsiveContainer>

      <Legend
        entries={[
          { label: 'Scans started (free)', color: SCANS },
          { label: 'Escalations to paid transport', color: ESCALATIONS },
        ]}
      />
      <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
        Escalations are the closest proxy for billable ZenRows calls. Well above one per scan means the local
        stealth browser is being refused on nearly every request — worth fixing at the source, since it is
        cheaper than paying per page.{' '}
        <span style={{ color: STATUS.warning }}>Log retention limits this history to 30 days.</span>
      </div>
    </>
  );
}
